import { useState, useEffect, useRef, useCallback } from "react";
import { getToken } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useIsTouch } from "@/hooks/use-mobile";

const CURSOR_THROTTLE_MS = 80;

/**
 * Conexión WebSocket de cursores en tiempo real.
 * Envía la posición del cursor (throttled) con nombre/color/avatar actuales y pinta los cursores remotos.
 */
export function CursorPresence() {
  const { user } = useAuth();
  const { showCursors, getDisplayName } = useSettings();
  const isTouch = useIsTouch();
  const [remoteCursors, setRemoteCursors] = useState({});
  const wsRef = useRef(null);
  const lastSendRef = useRef(0);
  const rafRef = useRef(null);

  const sendPosition = useCallback((x, y) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastSendRef.current < CURSOR_THROTTLE_MS) return;
    lastSendRef.current = now;
    try {
      const payload = {
        type: "cursor",
        x: Math.round(x),
        y: Math.round(y),
        name: getDisplayName(user) ?? user?.nombre ?? user?.email,
        color: user?.color,
        avatarVariant: user?.avatar_variant,
      };
      wsRef.current.send(JSON.stringify(payload));
    } catch (_) {}
  }, [user?.nombre, user?.email, user?.color, user?.avatar_variant]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/api/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "cursor" && data.userId) {
          setRemoteCursors((prev) => ({
            ...prev,
            [data.userId]: {
              name: data.name ?? data.userId,
              color: data.color,
              avatarVariant: data.avatarVariant,
              x: data.x,
              y: data.y,
              updatedAt: Date.now(),
            },
          }));
        } else if (data.type === "favorito_update" && data.candidato_id && Array.isArray(data.favoritos_por_detalle)) {
          window.dispatchEvent(new CustomEvent("candidatos-favorito-update", {
            detail: { candidatoId: data.candidato_id, favoritos_por_detalle: data.favoritos_por_detalle },
          }));
        } else if (data.type === "lista_updated" && data.lista_id) {
          window.dispatchEvent(new CustomEvent("lista-updated", { detail: { listaId: data.lista_id } }));
        }
      } catch (_) {}
    };

    ws.onclose = () => {
      wsRef.current = null;
    };

    const onMouseMove = (e) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => sendPosition(e.clientX, e.clientY));
    };

    const trackMouse = showCursors && !isTouch;
    if (trackMouse) {
      window.addEventListener("mousemove", onMouseMove, { passive: true });
    }

    return () => {
      if (trackMouse) {
        window.removeEventListener("mousemove", onMouseMove);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setRemoteCursors({});
    };
  }, [showCursors, isTouch, sendPosition]);

  // Limpiar cursores que no se actualizan (usuario desconectado)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const timeout = 3000;
      setRemoteCursors((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const [id, c] of Object.entries(next)) {
          if (now - (c.updatedAt || 0) > timeout) {
            delete next[id];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const entries = Object.entries(remoteCursors).filter(([userId]) => userId !== user?.email);
  // En táctil las coordenadas de escritorio no tienen sentido: el WebSocket sigue vivo, pero no se pintan cursores.
  if (!showCursors || isTouch || entries.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]" aria-hidden>
      {entries.map(([userId, c]) => (
        <div
          key={userId}
          className="absolute flex items-center gap-1.5 px-1.5 py-0.5 rounded-full bg-background/90 border border-border shadow-sm opacity-55"
          style={{
            left: c.x,
            top: c.y,
            transform: "translate(8px, 8px)",
          }}
        >
          <UserAvatar
            name={c.name}
            variant={c.avatarVariant}
            color={c.color}
            size={20}
            className="shrink-0"
          />
          <span className="text-xs font-medium text-foreground truncate max-w-[120px]">
            {c.name || userId}
          </span>
        </div>
      ))}
    </div>
  );
}
